import { useCallback, useEffect, useRef, useState } from "react";

import { useObjectMemo } from "../../../hooks";
import {
  parseErrorResponse,
  useRequestWithFeedback,
} from "../../../composites";
import { useLibrary } from "../../../providers";
import { useAppDispatch } from "../utils";

import { API } from "./api";
import { actions } from "./slice";
import { useFedwireTemplates } from "./hooks";

import type { BootstrapFetchRequest } from "../../bootstrap";

export const useFetchTemplates = () => {
  const { send, loading, status } = useRequestWithFeedback<
    API.FetchFedwireTemplates.Response,
    API.FetchFedwireTemplates.Error
  >();
  const [nextPage, setNextPage] = useState<number | null>(null);
  const accumulatedRef = useRef<API.FedwireTemplate[]>([]);
  const inFlightRef = useRef(false);

  const dispatch = useAppDispatch();

  const fetchPage = useCallback(
    (page: number) => {
      send({
        action: API.fetchTemplates(page),
        onData: ({ results: templates, next }) => {
          accumulatedRef.current = [...accumulatedRef.current, ...templates];
          if (next) {
            setNextPage(page + 1);
          } else {
            inFlightRef.current = false;
            setNextPage(null);
            dispatch(actions.upsertAll(accumulatedRef.current));
          }
        },
        onError: () => {
          inFlightRef.current = false;
          setNextPage(null);
        },
      });
    },
    [dispatch, send],
  );

  const fetchTemplates = useCallback(() => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    accumulatedRef.current = [];
    fetchPage(1);
  }, [fetchPage]);

  useEffect(() => {
    if (nextPage === null || loading) {
      return;
    }
    fetchPage(nextPage);
  }, [nextPage, loading, fetchPage]);

  const hasData = useFedwireTemplates().length > 0;

  return useObjectMemo({
    error: status === "error",
    send: fetchTemplates,
    loading: loading || nextPage !== null,
    hasData,
  }) satisfies BootstrapFetchRequest;
};

export const useDeleteTemplateRequest = () => {
  const { send, loading } = useRequestWithFeedback();

  const dispatch = useAppDispatch();
  const { throwToast } = useLibrary("toasts");
  const deleteTemplate = useCallback(
    (fedwireTemplateId: API.FedwireTemplateId) => {
      return send({
        messaging: {
          toast: {
            success: "Template deleted.",
          },
        },
        action: API.deleteTemplate(fedwireTemplateId),
        onSuccess: () => {
          dispatch(actions.deleteOne(fedwireTemplateId));
        },
        onError: async (error) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            throwToast({
              kind: "error",
              message: `${errors[0].description}`,
            });
          }
        },
      });
    },
    [dispatch, send, throwToast],
  );

  return useObjectMemo({
    deleteTemplate,
    loading,
  });
};
